import type { IntlShape } from 'react-intl';
import type { ToolUseItem } from '../../../../contracts/harness.types';
import { sharedAgentMessages } from '../../../../shared.messages';

import type { ObjectCardVariant, ObjectCreationKind } from './object-creation.types';
import { titleFromRecord } from './objectWrites';

export {
  directObjectTitle,
  inputTitleKeysFor,
  isObjectCreationToolName,
  OBJECT_TOOL_NAMES,
  objectKindForTool,
  resultTitleKeysFor,
  titleFromRecord,
} from './objectWrites';

/**
 * Default card treatment for the Projects chat. Both variants ship (see
 * `object-creation.styles.ts`); flip this single constant to swap the look
 * across every created-object card without a UI toggle.
 * - `variant-1`: accent fill, no border.
 * - `variant-2`: bordered chat-surface fill (blends into the transcript).
 */
export const DEFAULT_OBJECT_CARD_VARIANT: ObjectCardVariant = 'variant-2';

/** "Open …" affordance label for a completed object card. */
export const objectActionLabel = (kind: ObjectCreationKind, intl: IntlShape): string => {
  switch (kind) {
    case 'session':
      return intl.formatMessage({ defaultMessage: 'Open session', id: 'lTKuAGoTLG' });
    case 'patient':
      return intl.formatMessage({ defaultMessage: 'Open patient', id: 'tGFcb1cjVR' });
    case 'document':
      return intl.formatMessage({ defaultMessage: 'Open document', id: 'I6j6tFVunS' });
    case 'task':
      return intl.formatMessage({ defaultMessage: 'Open tasks', id: '2cVj2C+yK1' });
    case 'template':
      return intl.formatMessage({ defaultMessage: 'Open template', id: 'zjKk7mRb9I' });
  }
};

/** Fallback title when an object was created without a resolvable name. */
export const untitledTitle = (kind: ObjectCreationKind, intl: IntlShape): string => {
  switch (kind) {
    case 'session':
      return intl.formatMessage(sharedAgentMessages.untitledSession);
    case 'document':
      return intl.formatMessage({
        defaultMessage: 'Untitled document',
        id: '6PDFuGEPVW',
      });
    case 'patient':
      return intl.formatMessage({ defaultMessage: 'Unnamed patient', id: 'zNZ0Neuljc' });
    case 'task':
      return intl.formatMessage({ defaultMessage: 'Untitled task', id: 'RuyWs2JrH9' });
    case 'template':
      return intl.formatMessage({
        defaultMessage: 'Untitled template',
        id: 'qoDdETh9Ld',
      });
  }
};

const SESSION_DATE_INPUT_KEYS = ['scheduled_time', 'start_time', 'session_date'];
const DOB_INPUT_KEYS = ['date_of_birth', 'dob', 'birth_date', 'birthDate'];

/**
 * Secondary line for a created-object row. Patients get "DOB <date>" when the
 * create call carried one; sessions show their scheduled date when available.
 *
 * NOTE: the patient date-of-birth this returns is PHI — for in-app display
 * only, never for logs/analytics/URLs.
 */
export const deriveObjectSubtitle = (
  item: ToolUseItem,
  kind: ObjectCreationKind,
  intl: IntlShape
): string | undefined => {
  const input = item.input ?? {};
  if (kind === 'session') {
    const value = titleFromRecord(input, SESSION_DATE_INPUT_KEYS);
    if (!value) return undefined;
    const date = new Date(value);
    return Number.isNaN(date.getTime())
      ? undefined
      : intl.formatDate(date, { day: 'numeric', month: 'short' });
  }
  if (kind === 'patient') {
    const dob = titleFromRecord(input, DOB_INPUT_KEYS);
    if (dob) {
      return intl.formatMessage(
        { defaultMessage: 'DOB {dob}', id: 'ReeGNQoDQQ' },
        { dob }
      );
    }
  }
  return undefined;
};

/**
 * Two-letter avatar initials from a display name. May be derived from a
 * patient name (PHI) — render-only, never log/serialise.
 */
export const deriveInitials = (name: string): string | undefined => {
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return undefined;
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  const initials = `${first}${last}`.toUpperCase();
  return initials || undefined;
};
